import { Inject, Injectable } from '@angular/core';
import { Observable, of, Subject, switchMap } from 'rxjs';
import { PhoneVerificationReq } from 'src/app/shared/components/phone-verification-dialog/PhoneVerificationReq.model';
import { PhoneVerificationReqRes } from 'src/app/shared/components/phone-verification-dialog/PhoneVerificationReqRes.model';
import { UnitBusinessVirtualDebitCardDto } from 'src/app/shared/models/unit-card/UnitBusinessVirtualDebitCardDto.model';
import { UnitCustomerTokenDto } from 'src/app/shared/models/unit-card/UnitCustomerTokenDto.model';
import { AbstractRestService } from 'src/app/shared/types/AbstractRestService';
import { HttpService } from '../http.service';
import { UnitPinStatus } from 'src/app/shared/models/unit-card/unit-pin-status.enum';

@Injectable()
export class UnitCardDataService extends AbstractRestService<any, any> {
  readonly baseEndpoint$ = of('unit-card');

  constructor(
    // @Inject('UnitCardBaseEndpoint') public baseEndpoint$:  Observable<string>,
    public http: HttpService
  ) {
    super(http);
  }

  getBusinessDebitCardDraft(
    unitBankAccountId: number
  ): Observable<UnitBusinessVirtualDebitCardDto> {
    return this.baseEndpoint$.pipe(
      switchMap((base) =>
        this.http.getTyped<UnitBusinessVirtualDebitCardDto>(
          `${base}/draft/${unitBankAccountId}`
        )
      )
    );
  }

  getBusinessDebitCardByBankAccount(
    unitBankAccountId: number
  ): Observable<UnitBusinessVirtualDebitCardDto[]> {
    return this.baseEndpoint$.pipe(
      switchMap((base) =>
        this.http.getTyped<UnitBusinessVirtualDebitCardDto[]>(
          `${base}/bank-account-debit-card/${unitBankAccountId}`
        )
      )
    );
  }

  saveBusinessDebitCardDraft(
    unitBankAccountId: number,
    model: UnitBusinessVirtualDebitCardDto
  ): Observable<UnitBusinessVirtualDebitCardDto> {
    return this.baseEndpoint$.pipe(
      switchMap((base) =>
        this.http.postTyped<UnitBusinessVirtualDebitCardDto>(
          `${base}/${unitBankAccountId}/business`,
          model
        )
      )
    );
  }

  createBusinessDebitCardOnUnit(
    unitBankAccountId: number,
    businessCardId: number
  ): Observable<UnitBusinessVirtualDebitCardDto> {
    return this.baseEndpoint$.pipe(
      switchMap((base) =>
        this.http.postTyped<UnitBusinessVirtualDebitCardDto>(
          `${base}/${unitBankAccountId}/send-to-unit/${businessCardId}`,
          null
        )
      )
    );
  }

  updateBusinessDebitCard(
    unitBankAccountId: number,
    businessCardId: number,
    model: UnitBusinessVirtualDebitCardDto
  ): Observable<UnitBusinessVirtualDebitCardDto> {
    return this.baseEndpoint$.pipe(
      switchMap((base) =>
        this.http.putTyped<UnitBusinessVirtualDebitCardDto>(
          `${base}/${unitBankAccountId}/${businessCardId}`,
          model
        )
      )
    );
  }

  sendVerificationCode(
    businessCardId: number
  ): Observable<PhoneVerificationReqRes> {
    return this.baseEndpoint$.pipe(
      switchMap((base) =>
        this.http.postTyped<PhoneVerificationReqRes>(
          `${base}/${businessCardId}/phone-verification/send-code`,
          null
        )
      )
    );
  }

  resendVerificationCode(
    businessCardId: number
  ): Observable<PhoneVerificationReqRes> {
    return this.baseEndpoint$.pipe(
      switchMap((base) =>
        this.http.postTyped<PhoneVerificationReqRes>(
          `${base}/${businessCardId}/phone-verification/resend-code`,
          null
        )
      )
    );
  }

  verifyPhone(
    businessCardId: number,
    request: PhoneVerificationReq
  ): Observable<PhoneVerificationReqRes> {
    return this.baseEndpoint$.pipe(
      switchMap((base) =>
        this.http.postTyped<PhoneVerificationReqRes>(
          `${base}/${businessCardId}/phone-verification/verify`,
          request
        )
      )
    );
  }

  createBusinessVirtualDebitCardVerificationToken(
    businessCardId: number
  ): Observable<UnitCustomerTokenDto> {
    return this.baseEndpoint$.pipe(
      switchMap((base) =>
        this.http.postTyped<UnitCustomerTokenDto>(
          `${base}/${businessCardId}/business-customer-verification`,
          null
        )
      )
    );
  }

  createCardCustomerTokenAsync(
    businessCardId: number,
    model: UnitCustomerTokenDto
  ): Observable<UnitCustomerTokenDto> {
    return this.baseEndpoint$.pipe(
      switchMap((base) =>
        this.http.postTyped<UnitCustomerTokenDto>(
          `${base}/${businessCardId}/create-customer-token`,
          model
        )
      )
    );
  }

  freezeBusinessDebitCard(
    businessCardId: number
  ): Observable<UnitBusinessVirtualDebitCardDto> {
    return this.baseEndpoint$.pipe(
      switchMap((base) =>
        this.http.postTyped<UnitBusinessVirtualDebitCardDto>(
          `${base}/${businessCardId}/freeze`,
          null
        )
      )
    );
  }

  unFreezeBusinessDebitCard(
    businessCardId: number
  ): Observable<UnitBusinessVirtualDebitCardDto> {
    return this.baseEndpoint$.pipe(
      switchMap((base) =>
        this.http.postTyped<UnitBusinessVirtualDebitCardDto>(
          `${base}/${businessCardId}/unfreeze`,
          null
        )
      )
    );
  }

  getBusinessDebitCardPinCodeStatus(
    businessCardId: number
  ): Observable<UnitPinStatus> {
    return this.baseEndpoint$.pipe(
      switchMap((base) =>
        this.http.getTyped<UnitPinStatus>(
          `${base}/${businessCardId}/pin-code-status`
        )
      )
    );
  }

  cancelBusinessDebitCard(
    businessCardId: number
  ): Observable<UnitBusinessVirtualDebitCardDto> {
    return this.baseEndpoint$.pipe(
      switchMap((base) =>
        this.http.postTyped<UnitBusinessVirtualDebitCardDto>(
          `${base}/${businessCardId}/cancel`,
          null
        )
      )
    );
  }
}
